import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import dayjs from '@/lib/dayjs'
import {
  getPriceAdjustmentSameStartNoticeText,
  getPriceAdjustmentSignedAmountLabel,
  getPriceAdjustmentUpcomingSameStartNotice,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { useRef, useState } from 'react'
import {
  formatActiveWindowEndDate,
  formatBoundaryDate,
  getAdjustmentIds,
  getBoundaryTimestamp,
  getCurrentCollectionOverrideGroups,
  getLatestStartedAdjustment,
  getNextEndingAdjustments,
  getPriceAdjustmentNoticeLabel,
  getPriceAdjustmentNoticeListLabel,
  getPriceAdjustmentRuleListLabel,
  getResumingBaseAdjustment,
  getScheduledBoardSortTimestamp,
  getUniqueCollections,
  sortScheduledBoardAdjustments,
} from './-price-change-board'
import { CurrentPricingDetailsDialog } from './-price-change-current-details-dialog'
import { PriceChangeSnapshot } from './-price-change-snapshot'
import { PriceChangeTimelinePanel } from './-price-change-timeline'

function HandoffSentenceLink({ adjustmentIds = [], children, onHighlightAdjustments }) {
  const targetAdjustmentIds = [...new Set(adjustmentIds.map(Number))].filter(
    (adjustmentId) => adjustmentId > 0
  )

  if (targetAdjustmentIds.length === 0 || !onHighlightAdjustments) {
    return <>{children}</>
  }

  return (
    <Button
      variant="link"
      size="inline"
      onClick={() => onHighlightAdjustments(targetAdjustmentIds)}
    >
      {children}
    </Button>
  )
}

function SameStartNoticeSentence({ notice, dateLabel, onHighlightAdjustments }) {
  if (!notice) {
    return null
  }

  const noticeText = getPriceAdjustmentSameStartNoticeText(notice, { dateLabel })
  const allAdjustmentIds = getAdjustmentIds(notice.adjustments ?? [])
  const winnerAdjustmentIds = getAdjustmentIds([notice.winner])
  const adjustmentLabel = getPriceAdjustmentNoticeListLabel(notice.adjustments ?? [])
  const winnerLabel = getPriceAdjustmentNoticeLabel(notice.winner)

  if (!adjustmentLabel || !winnerLabel) {
    return <>{noticeText}</>
  }

  return (
    <>
      {dateLabel ? <>On {dateLabel}, </> : null}
      <HandoffSentenceLink
        adjustmentIds={allAdjustmentIds}
        onHighlightAdjustments={onHighlightAdjustments}
      >
        {adjustmentLabel}
      </HandoffSentenceLink>{' '}
      start on the same day, so only{' '}
      <HandoffSentenceLink
        adjustmentIds={winnerAdjustmentIds}
        onHighlightAdjustments={onHighlightAdjustments}
      >
        {winnerLabel}
      </HandoffSentenceLink>{' '}
      will be applied.
    </>
  )
}

function getCollectionSubjectText(collections = []) {
  const uniqueCollections = getUniqueCollections(collections)

  if (uniqueCollections.length === 0) {
    return 'Selected collections'
  }

  if (uniqueCollections.length === 1) {
    return `${uniqueCollections[0].name} collection`
  }

  if (uniqueCollections.length === 2) {
    return `${uniqueCollections[0].name} and ${uniqueCollections[1].name} collections`
  }

  return `${uniqueCollections[0].name}, ${uniqueCollections[1].name}, and ${
    uniqueCollections.length - 2
  } more collections`
}

function getCollectionOutsideText(collections = []) {
  const uniqueCollections = getUniqueCollections(collections)

  return uniqueCollections.length === 1 ? 'that collection' : 'those collections'
}

function getSnapshotOverrideGroupLabel({ adjustment, collections = [] }) {
  const title = getPriceAdjustmentNoticeLabel(adjustment)
  const uniqueCollections = getUniqueCollections(collections)
  const collectionCount = uniqueCollections.length

  if (!title) {
    return getCollectionSubjectText(collections)
  }

  if (collectionCount === 0) {
    return title
  }

  if (collectionCount === 1) {
    const collectionName = uniqueCollections[0]?.name

    if (collectionName && title.trim().toLowerCase() === collectionName.trim().toLowerCase()) {
      return `${title} collection`
    }

    return collectionName ? `${title} (${collectionName} collection)` : title
  }

  return `${title} (${collectionCount} collections)`
}

function formatSnapshotOverrideGroupList(groups = []) {
  const labels = groups.map(getSnapshotOverrideGroupLabel).filter(Boolean)

  if (labels.length === 0) {
    return 'Selected collections'
  }

  if (labels.length === 1) {
    return labels[0]
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`
  }

  return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`
}

export function PriceChangeStackStatus({
  activeAdjustments = [],
  adjustments = [],
  currentAction = null,
  hideSnapshot = false,
  isTimelineOpen = false,
  onHighlightAdjustments,
  onDismiss,
  onTimelineOpenChange,
}) {
  const timelineInitialFocusRef = useRef(null)
  const [isCurrentDetailsOpen, setIsCurrentDetailsOpen] = useState(false)
  const activeBaseAdjustments = activeAdjustments.filter(
    (adjustment) => adjustment?.scope_type === 'global'
  )
  const activeBaseAdjustment = getLatestStartedAdjustment(activeBaseAdjustments)
  const activeOverrideAdjustments = activeAdjustments.filter(
    (adjustment) => adjustment?.scope_type === 'collections'
  )
  const activeOverrideGroups = getCurrentCollectionOverrideGroups(activeOverrideAdjustments)
  const applyingOverrideAdjustments = activeOverrideGroups.map(({ adjustment }) => adjustment)
  const hasCollectionOverrideDetails = applyingOverrideAdjustments.length > 0
  const nextEndingOverrides = getNextEndingAdjustments(applyingOverrideAdjustments)
  const nextEndingOverride = nextEndingOverrides[0] ?? null
  const activeBaseEndDate = formatActiveWindowEndDate(activeBaseAdjustment?.active_window?.ends_at)
  const activeBaseBoundaryDate = formatBoundaryDate(activeBaseAdjustment?.active_window?.ends_at)
  const nextOverrideEndDate = formatActiveWindowEndDate(nextEndingOverride?.active_window?.ends_at)
  const nextOverrideEndTimestamp = getBoundaryTimestamp(nextEndingOverride?.active_window?.ends_at)
  const activeBaseEndTimestamp = getBoundaryTimestamp(activeBaseAdjustment?.active_window?.ends_at)
  const scheduledAdjustments = sortScheduledBoardAdjustments(
    adjustments.filter((adjustment) => ['scheduled', 'draft'].includes(adjustment?.status))
  )
  const nextScheduledStartTimestamp = getScheduledBoardSortTimestamp(scheduledAdjustments[0])
  const nextScheduledStartAdjustments =
    nextScheduledStartTimestamp === null
      ? []
      : scheduledAdjustments.filter(
          (adjustment) => getScheduledBoardSortTimestamp(adjustment) === nextScheduledStartTimestamp
        )
  const nextScheduledStartDate =
    nextScheduledStartTimestamp === null ? null : dayjs(nextScheduledStartTimestamp).format('MMM D')
  const nextScheduledSameStartNotice =
    nextScheduledStartAdjustments.length > 0
      ? getPriceAdjustmentUpcomingSameStartNotice(nextScheduledStartAdjustments)
      : null
  const nextScheduledStartAdjustmentIds = getAdjustmentIds(nextScheduledStartAdjustments)
  const activeBaseAdjustmentIds = getAdjustmentIds([activeBaseAdjustment])
  const applyingOverrideAdjustmentIds = getAdjustmentIds(applyingOverrideAdjustments)
  const resumingBaseAdjustment = getResumingBaseAdjustment(
    activeBaseAdjustments,
    activeBaseAdjustment
  )
  const resumingBaseAdjustmentIds = getAdjustmentIds([resumingBaseAdjustment])
  const activeBaseWindowLabel = activeBaseEndDate
    ? `through ${activeBaseEndDate}`
    : 'with no automatic end'
  const activeBaseAmountLabel = activeBaseAdjustment
    ? getPriceAdjustmentSignedAmountLabel(activeBaseAdjustment)
    : null
  const activeBaseNoticeLabel = activeBaseAdjustment
    ? getPriceAdjustmentNoticeLabel(activeBaseAdjustment)
    : null
  const activeOverrideSnapshotLabel = formatSnapshotOverrideGroupList(activeOverrideGroups)
  const activeOverrideOutsideLabel = getCollectionOutsideText(
    activeOverrideGroups.flatMap(({ collections }) => collections ?? [])
  )
  const activeOverrideSnapshotCount = activeOverrideGroups.length
  const getNextScheduledStartSummary = () => {
    if (!nextScheduledStartDate) {
      return {
        body: <>No additional price changes are queued next.</>,
        tone: 'pricing',
      }
    }

    if (nextScheduledSameStartNotice) {
      return {
        body: (
          <SameStartNoticeSentence
            notice={nextScheduledSameStartNotice}
            dateLabel={nextScheduledStartDate}
            onHighlightAdjustments={onHighlightAdjustments}
          />
        ),
        tone: 'warning',
      }
    }

    if (nextScheduledStartAdjustmentIds.length > 0) {
      return {
        body: (
          <>
            On {nextScheduledStartDate},{' '}
            <HandoffSentenceLink
              adjustmentIds={nextScheduledStartAdjustmentIds}
              onHighlightAdjustments={onHighlightAdjustments}
            >
              {getPriceAdjustmentRuleListLabel(nextScheduledStartAdjustments)}
            </HandoffSentenceLink>{' '}
            {nextScheduledStartAdjustments.length === 1 ? 'starts' : 'start'}.
          </>
        ),
        tone: 'pricing',
      }
    }

    return {
      body: <>On {nextScheduledStartDate}, the next scheduled price change starts.</>,
      tone: 'pricing',
    }
  }
  const nextScheduledStartSummary = getNextScheduledStartSummary()
  const timelineDialog = isTimelineOpen ? (
    <Dialog open={isTimelineOpen} onOpenChange={onTimelineOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        size="wide"
        padding="none"
        viewport="contained"
        className="max-h-[calc(100vh-3rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)]"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          timelineInitialFocusRef.current?.focus()
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Price change timeline</DialogTitle>
        </DialogHeader>
        <div
          ref={timelineInitialFocusRef}
          id="price-change-full-timeline"
          data-testid="price-change-timeline-modal"
          tabIndex={-1}
          className="max-h-[calc(100vh-3rem)] overflow-y-auto p-1 focus:outline-none"
        >
          <PriceChangeTimelinePanel
            onHighlightAdjustments={onHighlightAdjustments}
            onClose={() => onTimelineOpenChange(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  ) : null
  const currentDetailsDialog = hasCollectionOverrideDetails ? (
    <CurrentPricingDetailsDialog
      activeAdjustments={activeAdjustments}
      isOpen={isCurrentDetailsOpen}
      onHighlightAdjustments={onHighlightAdjustments}
      onOpenChange={setIsCurrentDetailsOpen}
    />
  ) : null
  const hasActiveSnapshot = Boolean(activeBaseAdjustment) || applyingOverrideAdjustments.length > 0

  const currentSnapshotBody = hasActiveSnapshot ? (
    <>
      {activeBaseAdjustment ? (
        <>
          <HandoffSentenceLink
            adjustmentIds={activeBaseAdjustmentIds}
            onHighlightAdjustments={onHighlightAdjustments}
          >
            {activeBaseNoticeLabel}
          </HandoffSentenceLink>{' '}
          is {activeBaseAmountLabel} {activeBaseWindowLabel}.
        </>
      ) : null}
      {applyingOverrideAdjustments.length > 0 ? (
        <>
          {activeBaseAdjustment ? ' ' : null}
          The{' '}
          <HandoffSentenceLink
            adjustmentIds={applyingOverrideAdjustmentIds}
            onHighlightAdjustments={onHighlightAdjustments}
          >
            {activeOverrideSnapshotLabel}
          </HandoffSentenceLink>{' '}
          {activeOverrideSnapshotCount === 1 ? 'price change is' : 'price changes are'} active right
          now.
          {!activeBaseAdjustment ? (
            <> Regular product prices apply outside {activeOverrideOutsideLabel}.</>
          ) : null}
          {hasCollectionOverrideDetails ? (
            <>
              {' '}
              <Button variant="link" size="inline" onClick={() => setIsCurrentDetailsOpen(true)}>
                View collection details
              </Button>
              .
            </>
          ) : null}
        </>
      ) : null}
    </>
  ) : (
    <>No price changes are active right now.</>
  )

  const nextSnapshot = (() => {
    if (!hasActiveSnapshot) {
      if (!nextScheduledStartDate) {
        return { body: null, tone: 'pricing' }
      }

      return nextScheduledStartSummary
    }

    if (applyingOverrideAdjustments.length > 0 && nextOverrideEndTimestamp !== null) {
      if (
        nextScheduledStartTimestamp !== null &&
        nextScheduledStartTimestamp < nextOverrideEndTimestamp
      ) {
        return nextScheduledStartSummary
      }

      if (
        nextScheduledStartTimestamp !== null &&
        nextScheduledStartTimestamp === nextOverrideEndTimestamp &&
        nextScheduledSameStartNotice
      ) {
        return nextScheduledStartSummary
      }

      return {
        body: (
          <>
            On {nextOverrideEndDate}, {getPriceAdjustmentRuleListLabel(nextEndingOverrides)}{' '}
            {nextEndingOverrides.length === 1 ? 'ends' : 'end'}, and those products go back to{' '}
            {activeBaseAdjustment ? (
              <HandoffSentenceLink
                adjustmentIds={activeBaseAdjustmentIds}
                onHighlightAdjustments={onHighlightAdjustments}
              >
                {getPriceAdjustmentNoticeLabel(activeBaseAdjustment)}
              </HandoffSentenceLink>
            ) : (
              <>regular product prices</>
            )}
            .
          </>
        ),
        tone: 'pricing',
      }
    }

    if (applyingOverrideAdjustments.length > 0) {
      if (
        nextScheduledStartTimestamp !== null &&
        (activeBaseEndTimestamp === null || nextScheduledStartTimestamp <= activeBaseEndTimestamp)
      ) {
        return nextScheduledStartSummary
      }

      if (activeBaseEndTimestamp !== null) {
        return {
          body: (
            <>
              On {activeBaseBoundaryDate},{' '}
              <HandoffSentenceLink
                adjustmentIds={activeBaseAdjustmentIds}
                onHighlightAdjustments={onHighlightAdjustments}
              >
                {getPriceAdjustmentRuleListLabel([activeBaseAdjustment])}
              </HandoffSentenceLink>{' '}
              ends, and everything outside those collections goes back to{' '}
              {resumingBaseAdjustment ? (
                <HandoffSentenceLink
                  adjustmentIds={resumingBaseAdjustmentIds}
                  onHighlightAdjustments={onHighlightAdjustments}
                >
                  {getPriceAdjustmentNoticeLabel(resumingBaseAdjustment)}
                </HandoffSentenceLink>
              ) : (
                <>regular product prices</>
              )}
              .
            </>
          ),
          tone: 'pricing',
        }
      }

      return {
        body: <>No additional price changes are queued next.</>,
        tone: 'pricing',
      }
    }

    if (
      nextScheduledStartTimestamp !== null &&
      (activeBaseEndTimestamp === null || nextScheduledStartTimestamp <= activeBaseEndTimestamp)
    ) {
      return nextScheduledStartSummary
    }

    if (activeBaseBoundaryDate) {
      if (resumingBaseAdjustment) {
        return {
          body: (
            <>
              On {activeBaseBoundaryDate}, {getPriceAdjustmentRuleListLabel([activeBaseAdjustment])}{' '}
              ends, and prices go back to{' '}
              <HandoffSentenceLink
                adjustmentIds={resumingBaseAdjustmentIds}
                onHighlightAdjustments={onHighlightAdjustments}
              >
                {getPriceAdjustmentNoticeLabel(resumingBaseAdjustment)}
              </HandoffSentenceLink>
              .
            </>
          ),
          tone: 'pricing',
        }
      }

      return {
        body: (
          <>
            On {activeBaseBoundaryDate}, {getPriceAdjustmentRuleListLabel([activeBaseAdjustment])}{' '}
            ends, and prices go back to regular product prices.
          </>
        ),
        tone: 'pricing',
      }
    }

    return {
      body: <>No additional price changes are queued next.</>,
      tone: 'pricing',
    }
  })()
  const nextSnapshotBody = nextSnapshot.body

  if (!hasActiveSnapshot && !nextScheduledStartDate) {
    return null
  }

  const snapshotContent = hideSnapshot ? null : (
    <PriceChangeSnapshot
      currentBody={currentSnapshotBody}
      currentAction={currentAction}
      nextBody={nextSnapshotBody}
      currentTone={hasActiveSnapshot ? 'current' : 'neutral'}
      nextTone={nextSnapshotBody ? 'next' : 'neutral'}
      tone={nextSnapshot.tone}
      onDismiss={onDismiss}
    />
  )

  return (
    <div className="relative">
      {snapshotContent}
      {currentDetailsDialog}
      {timelineDialog}
    </div>
  )
}
